import { useEffect, useRef, useCallback, useMemo } from "react";
import * as Sentry from "../lib/errorReporting";
import { supabase } from "../lib/supabase";
import { App } from "@capacitor/app";
import { useRealtimeChannel } from "./useRealtimeChannel";
import { chunk, BetLoadError, LEGS_SEED_CHUNK, LEGS_SEED_ROW_CAP } from "../lib/bet-load";

/**
 * Read a cron-owned column from a bet_legs row, keeping the client value when
 * the key is absent. #1544: under REPLICA IDENTITY DEFAULT an UPDATE that leaves
 * a TOASTed value unchanged arrives in payload.new without that column.
 */
function cronField(row, key, prev) {
  return key in row ? (row[key] ?? null) : (prev ?? null);
}

/**
 * Patch bets state with fresh bet_legs rows from Supabase.
 * Matches by bet_id + leg_index.
 *
 * Always patch when the key is present (cron-owned — DB is authoritative even when null):
 *   statValue, liveContext, frozenStat, frozenDnp, status (from leg_status),
 *   trackingStatus, untrackedReason (never-reject PR 3: a Project B flip to
 *   tracked arrives as null and must clear the "Not tracked yet" chip)
 *   An absent key means unchanged, not null (#1544: see cronField).
 *
 * Patch when DB value is non-null (identity/display fields):
 *   gameId, teams, pick, market/type/canonicalMarket, line, odds,
 *   overUnder, player/playerName, statType, fullName,
 *   rawMarketText, rawSelectionText (never-reject PR 3)
 *
 * Referential stability: returns prevBets unchanged (same reference)
 * when no field actually changed.
 */
function applyLegPatches(prevBets, legRows) {
  const byBetId = new Map();
  for (const row of legRows) {
    const key = String(row.bet_id);
    if (!byBetId.has(key)) byBetId.set(key, []);
    byBetId.get(key).push(row);
  }

  let anyBetChanged = false;
  const result = prevBets.map((bet) => {
    if (bet.settledAt && !bet.legs?.some(l => l.status == null)) return bet;
    if (!bet.legs) return bet;
    const rows = byBetId.get(String(bet.id));
    if (!rows) return bet;

    const rowByIndex = new Map(rows.map((r) => [r.leg_index, r]));
    let changed = false;
    const patchedLegs = bet.legs.map((leg, i) => {
      const row = rowByIndex.get(i);
      if (!row) return leg;

      // --- Cron-owned fields (apply when present, DB authoritative even when null;
      // absent key = unchanged, #1544) ---
      const newStatValue = cronField(row, "stat_value", leg.statValue);
      const newLiveContext = cronField(row, "live_context", leg.liveContext);
      const newFrozenStat = cronField(row, "frozen_stat", leg.frozenStat);
      const newFrozenDnp = "frozen_dnp" in row
        ? (row.frozen_dnp === true ? true : null) // #1353 PR 3b
        : (leg.frozenDnp ?? null);
      const newStatus = cronField(row, "leg_status", leg.status);
      const newTrackingStatus = cronField(row, "tracking_status", leg.trackingStatus);
      const newUntrackedReason = cronField(row, "untracked_reason", leg.untrackedReason);

      // --- Identity/display fields (only patch when DB value is non-null and non-empty) ---
      // "" is a known missing sentinel for game IDs, teams, and pick alongside null/undefined.
      const newGameId =
        row.game_id != null && row.game_id !== "" ? (Number(row.game_id) || row.game_id) : undefined;
      const newTeams = row.teams != null && row.teams !== "" ? row.teams : undefined;
      const newPick = row.pick != null && row.pick !== "" ? row.pick : undefined;
      const newMarket = row.market != null ? row.market : undefined;
      const newLine = row.line != null ? row.line : undefined;
      const newOdds = row.odds != null ? row.odds : undefined;
      const newOverUnder = row.over_under != null ? row.over_under : undefined;
      const newPlayerName = row.player_name != null ? row.player_name : undefined;
      const newStatType = row.stat_type != null ? row.stat_type : undefined;
      const newFullName = row.full_name != null ? row.full_name : undefined;
      const newRawMarketText = row.raw_market_text != null ? row.raw_market_text : undefined;
      const newRawSelectionText = row.raw_selection_text != null ? row.raw_selection_text : undefined;

      // Equality check — avoid allocating a new object if nothing changed
      const cronSame =
        (leg.statValue ?? null) === newStatValue &&
        JSON.stringify(leg.liveContext ?? null) === JSON.stringify(newLiveContext) &&
        (leg.frozenStat ?? null) === newFrozenStat &&
        (leg.frozenDnp ?? null) === newFrozenDnp &&
        (leg.status ?? null) === newStatus &&
        (leg.trackingStatus ?? null) === newTrackingStatus &&
        (leg.untrackedReason ?? null) === newUntrackedReason;

      const identitySame =
        (newGameId === undefined || String(leg.gameId ?? "") === String(newGameId)) &&
        (newTeams === undefined || leg.teams === newTeams) &&
        (newPick === undefined || leg.pick === newPick) &&
        // All three market aliases must match — a stale type/canonicalMarket is still a change
        (newMarket === undefined || (leg.market === newMarket && leg.type === newMarket && leg.canonicalMarket === newMarket)) &&
        (newLine === undefined || leg.line === newLine) &&
        (newOdds === undefined || leg.odds === newOdds) &&
        (newOverUnder === undefined || leg.overUnder === newOverUnder) &&
        // Both player aliases must match — a stale player field is still a change
        (newPlayerName === undefined || (leg.playerName === newPlayerName && leg.player === newPlayerName)) &&
        (newStatType === undefined || leg.statType === newStatType) &&
        (newFullName === undefined || leg.fullName === newFullName) &&
        (newRawMarketText === undefined || leg.rawMarketText === newRawMarketText) &&
        (newRawSelectionText === undefined || leg.rawSelectionText === newRawSelectionText);

      if (cronSame && identitySame) return leg;

      changed = true;
      return {
        ...leg,
        // Cron-owned: overwrite (absent keys already resolved to the client value)
        statValue: newStatValue,
        liveContext: newLiveContext,
        frozenStat: newFrozenStat,
        frozenDnp: newFrozenDnp,
        status: newStatus,
        trackingStatus: newTrackingStatus,
        untrackedReason: newUntrackedReason,
        // Identity: only overwrite when DB has a value
        ...(newGameId !== undefined && { gameId: newGameId }),
        ...(newTeams !== undefined && { teams: newTeams }),
        ...(newPick !== undefined && { pick: newPick }),
        // market, type, canonicalMarket are renderer aliases for the same DB column
        ...(newMarket !== undefined && {
          market: newMarket,
          type: newMarket,
          canonicalMarket: newMarket,
        }),
        ...(newLine !== undefined && { line: newLine }),
        ...(newOdds !== undefined && { odds: newOdds }),
        ...(newOverUnder !== undefined && { overUnder: newOverUnder }),
        // player and playerName are renderer aliases for the same DB column
        ...(newPlayerName !== undefined && {
          player: newPlayerName,
          playerName: newPlayerName,
        }),
        ...(newStatType !== undefined && { statType: newStatType }),
        ...(newFullName !== undefined && { fullName: newFullName }),
        ...(newRawMarketText !== undefined && { rawMarketText: newRawMarketText }),
        ...(newRawSelectionText !== undefined && { rawSelectionText: newRawSelectionText }),
      };
    });
    if (!changed) return bet;

    const patchedGameIds = patchedLegs
      .map((leg) => leg.gameId)
      .filter((gameId) => gameId != null && gameId !== "");
    const uniquePatchedGameIds = [...new Set(patchedGameIds.map((gameId) => String(gameId)))];
    const shouldPatchParentGameId =
      !bet.gameId &&
      (bet.slipType === "single" || bet.slipType === "sgp") &&
      uniquePatchedGameIds.length === 1;

    anyBetChanged = true;
    return {
      ...bet,
      legs: patchedLegs,
      ...(shouldPatchParentGameId && { gameId: patchedGameIds[0] }),
    };
  });
  return anyBetChanged ? result : prevBets;
}

/**
 * Subscribe to Supabase bet_legs Realtime updates and seed on mount.
 *
 * @param {Object} params
 * @param {Object|null} params.user - current user (null = guest, skip)
 * @param {Array} params.bets - current bets array
 * @param {Function} params.setBets - state setter for bets
 */
export function useBetLegsRealtime({ user, bets, setBets }) {
  const betsRef = useRef(bets);
  betsRef.current = bets;
  const userRef = useRef(user);
  userRef.current = user;

  // Stable serialized key of unsettled bet IDs — drives seed re-run when bets
  // load, are added, or settle.
  const unsettledKey = useMemo(
    () =>
      bets
        .filter((b) => !b.settledAt && b.legs?.length)
        .map((b) => String(b.id))
        .sort()
        .join(","),
    [bets]
  );

  // Recently-settled bets with null-status legs (#336). Separate key so the
  // effect re-fires both when a bet drops out of unsettledKey (primary path)
  // AND when only recently-settled bets exist (no unsettled bets at all).
  const recentlySettledKey = useMemo(() => {
    const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
    return bets
      .filter(
        (b) =>
          b.settledAt &&
          b.settledAt > thirtyMinutesAgo &&
          b.legs?.some((l) => l.status == null)
      )
      .map((b) => String(b.id))
      .sort()
      .join(",");
  }, [bets]);

  const seed = useCallback(async () => {
    if (!supabase || !userRef.current) return;
    const unsettledIds = betsRef.current
      .filter((b) => !b.settledAt && b.legs?.length)
      .map((b) => String(b.id));

    // Include recently-settled bets whose legs still have null/undefined status (#336).
    // Covers the race where settledAt arrives before leg_status via Realtime.
    // Uses == null to also catch undefined from JSONB fallback path.
    const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
    const recentlySettledIds = betsRef.current
      .filter((b) =>
        b.settledAt &&
        b.settledAt > thirtyMinutesAgo &&
        b.legs?.some((l) => l.status == null)
      )
      .map((b) => String(b.id));

    const allIds = [...new Set([...unsettledIds, ...recentlySettledIds])];
    if (!allIds.length) return;

    try {
      // No .is("leg_status", null) filter: recovery must patch leg_status written
      // while Realtime was down. Seed fetches ALL bet_legs rows for tracked bet IDs.
      // #1554: chunked IN lists of <= 39 ids -- 39 x 25 legs (writer cap) = 975 rows, under the
      // lowest max-rows anywhere (1,000; Ultron G2). Chunks run in turn; rows are merged and
      // applied once; any failure applies nothing.
      const rows = [];
      for (const ids of chunk(allIds, LEGS_SEED_CHUNK)) {
        const { data, error } = await supabase
          .from("bet_legs")
          .select("*")
          .in("bet_id", ids);

        if (error) throw error;
        // A non-array answer is a failed chunk, never an empty one: apply nothing (#1554 pre-Codex F1).
        if (!Array.isArray(data)) throw new Error("bet_legs seed chunk: data is not an array");
        // A chunk this large may have been cut by the server cap: never apply a partial leg set.
        if (data.length >= LEGS_SEED_ROW_CAP) {
          throw new BetLoadError("seed_cap", `bet_legs seed chunk returned ${data.length} rows for ${ids.length} bets`);
        }
        rows.push(...data);
      }
      if (!rows.length) return;

      setBets((prev) => applyLegPatches(prev, rows));
    } catch (err) {
      if (err instanceof BetLoadError && err.reason === "seed_cap") {
        // Data integrity: legs may be missing and nothing retries differently (CC-IMPL-037).
        Sentry.captureException(err, {
          tags: { function: "useBetLegsRealtime", stage: "seed_cap" },
        });
      } else {
        // Recoverable: re-runs on the next foreground, channel recovery or bets change (#1554 Q6).
        Sentry.captureMessage("useBetLegsRealtime seed failed", {
          level: "warning",
          tags: { function: "useBetLegsRealtime", stage: "seed" },
          extra: { message: err?.message, code: err?.code },
        });
      }
    }
  }, [setBets]);

  // Re-seed when the tracked bet ID set changes (e.g. bets load after login,
  // new bet added, bet settled with stale legs). Runs after initial mount
  // AND on every change.
  useEffect(() => {
    if (!supabase || !user || (!unsettledKey && !recentlySettledKey)) return;
    seed();
  }, [user, unsettledKey, recentlySettledKey, seed]);

  // buildChannel: attaches postgres_changes handler. Must be stable (useCallback).
  const buildChannel = useCallback(
    (channel) =>
      channel.on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "bet_legs" },
        (payload) => {
          try {
            // #1544: Realtime answers a row it cannot deliver whole (401, 413 payload too large: only columns of
            // 64 bytes or less) with an `errors` stub -- re-seed rather than patch from it. Anything but a plain
            // object record is ignored (the `in` checks in the patch would throw inside the state updater).
            if (Array.isArray(payload.errors) && payload.errors.length > 0) {
              seed();
              return;
            }
            const row = payload.new;
            if (!row || typeof row !== "object" || Array.isArray(row)) return;
            setBets((prev) => applyLegPatches(prev, [row]));
          } catch (err) {
            Sentry.captureException(err, {
              tags: { function: "useBetLegsRealtime", stage: "realtime-event" },
            });
          }
        }
      ),
    [setBets, seed]
  );

  // Re-seed on recovery to backfill any updates missed during the outage.
  const onRecover = useCallback(() => {
    seed();
  }, [seed]);

  useRealtimeChannel({
    enabled: !!user,
    channelName: "bet-legs",
    sentryContext: { function: "useBetLegsRealtime", channel: "bet-legs" },
    buildChannel,
    onRecover,
  });

  // Capacitor foreground resume — re-seed to catch updates missed while backgrounded.
  // Separate effect so it can be cleaned up independently of the channel.
  useEffect(() => {
    if (!user) return;
    let listenerHandle = null;
    let cancelled = false;
    (async () => {
      try {
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) seed();
        });
        if (cancelled) {
          // Effect cleaned up before Promise resolved — remove immediately to prevent leak
          handle.remove();
          return;
        }
        listenerHandle = handle;
      } catch (err) {
        // Web environment — App.addListener may throw; non-fatal
        Sentry.captureException(err, {
          tags: { function: "useBetLegsRealtime", stage: "capacitor-listener" },
        });
      }
    })();

    return () => {
      cancelled = true;
      if (listenerHandle) listenerHandle.remove();
    };
  }, [user, seed]);
}
